import React, { useState, useMemo } from 'react';
import {
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  Select,
  SpaceBetween,
  Alert,
} from '@cloudscape-design/components';
import { DownloadButton } from './download';
import { useGetGRCReviewCycleSnapshotUrl } from 'src/api/hooks/useControlData';
import type { GetSnapshotCommandInput } from '@amzn/controllershiphub-typescript-client';
import { LEASE_TYPES } from 'src/constants/grc5253';

// Phase values sent to backend API (must match S3 folder names)
enum GRCReviewCyclePhase {
  MONTHLY = 'monthly',
  PHASE1 = 'phase1',
  PHASE2 = 'phase2',
}

// Constants
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const QUARTERLY_MONTHS = ['March', 'June', 'September', 'December'];
const SDO_START_YEAR = 2024;

/**
 * SDO snapshot availability map.
 * Each entry defines when a month's snapshot becomes available for download.
 * Based on CDK EventBridge schedule:
 * - Monthly: 9th of next month (e.g., Jan data available Feb 9)
 * - Phase 1: 1st of quarter-start month (e.g., Mar Phase 1 available Apr 1)
 * - Phase 2: 9th of quarter-start month (e.g., Mar Phase 2 available Apr 9)
 *
 * Format: { monthName: { phase: { availMonth, availDay } } }
 * availMonth is 0-indexed to match JavaScript Date: 0=Jan, 1=Feb, ..., 11=Dec
 */
const SDO_SNAPSHOT_AVAILABILITY: Record<
  string,
  Record<string, { availMonth: number; availDay: number; yearOffset?: number }>
> = (() => {
  const monthly = (nextMonth: number, day: number) => ({
    [GRCReviewCyclePhase.MONTHLY]: { availMonth: nextMonth, availDay: day },
  });
  const quarterEnd = (nextMonth: number) => ({
    [GRCReviewCyclePhase.PHASE1]: { availMonth: nextMonth, availDay: 1 },
    [GRCReviewCyclePhase.PHASE2]: { availMonth: nextMonth, availDay: 9 },
  });

  return {
    // Q1
    January: monthly(1, 9), // available Feb 9
    February: monthly(2, 9), // available Mar 9
    March: quarterEnd(3), // Phase 1: Apr 1, Phase 2: Apr 9
    // Q2
    April: monthly(4, 9), // available May 9
    May: monthly(5, 9), // available Jun 9
    June: quarterEnd(6), // Phase 1: Jul 1, Phase 2: Jul 9
    // Q3
    July: monthly(7, 9), // available Aug 9
    August: monthly(8, 9), // available Sep 9
    September: quarterEnd(9), // Phase 1: Oct 1, Phase 2: Oct 9
    // Q4
    October: monthly(10, 9), // available Nov 9
    November: monthly(11, 9), // available Dec 9
    December: {
      // Phase 1: Jan 1 next year, Phase 2: Jan 9 next year
      [GRCReviewCyclePhase.PHASE1]: { availMonth: 0, availDay: 1, yearOffset: 1 },
      [GRCReviewCyclePhase.PHASE2]: { availMonth: 0, availDay: 9, yearOffset: 1 },
    },
  };
})();

/**
 * Returns available months for a given year based on current date and availability rules.
 */
const getAvailableMonths = (
  year: string,
  currentYear: number,
  currentMonthIndex: number,
  currentDay: number
): string[] => {
  const selectedYear = parseInt(year, 10);
  const isStartYear = selectedYear === SDO_START_YEAR;

  if (selectedYear < currentYear) {
    // Past year: all months available (Oct-Dec for start year)
    return isStartYear ? MONTHS.slice(MONTHS.indexOf('October')) : MONTHS;
  }

  // Current or future year: check each month's availability
  return MONTHS.filter((month, monthIdx) => {
    if (isStartYear && monthIdx < MONTHS.indexOf('October')) return false;

    const availability = SDO_SNAPSHOT_AVAILABILITY[month];
    if (!availability) return false;

    // A month is available if ANY of its phases are available
    return Object.values(availability).some(({ availMonth, availDay, yearOffset }) => {
      const availYear = selectedYear + (yearOffset ?? 0);

      if (availYear < currentYear) return true;
      if (availYear > currentYear) return false;
      // Same year: check month and day
      if (availMonth < currentMonthIndex) return true;
      if (availMonth > currentMonthIndex) return false;
      return currentDay >= availDay;
    });
  });
};

/**
 * Returns phase options for a given month.
 * Non-quarterly months: only Monthly
 * Quarterly months (Mar/Jun/Sep/Dec): Phase 1 and Phase 2
 */
const getPhaseOptions = (
  month: string,
  selectedYear: string,
  currentYear: number,
  currentMonthIndex: number,
  currentDay: number
) => {
  const isQuarterlyMonth = QUARTERLY_MONTHS.includes(month);
  const yearNum = parseInt(selectedYear, 10);

  if (!isQuarterlyMonth) {
    return [{ label: 'Monthly', value: GRCReviewCyclePhase.MONTHLY }];
  }

  // Quarterly month: show Phase 1 and/or Phase 2 based on availability
  const options: { label: string; value: string }[] = [];
  const availability = SDO_SNAPSHOT_AVAILABILITY[month];

  for (const [phase, { availMonth, availDay, yearOffset }] of Object.entries(availability)) {
    const availYear = yearNum + (yearOffset ?? 0);

    const isAvailable =
      availYear < currentYear ||
      (availYear === currentYear && availMonth < currentMonthIndex) ||
      (availYear === currentYear && availMonth === currentMonthIndex && currentDay >= availDay);

    if (isAvailable) {
      const label = phase === GRCReviewCyclePhase.PHASE1 ? 'Phase 1' : 'Phase 2';
      options.push({ label, value: phase });
    }
  }

  return options;
};

const monthToNumber = (monthName: string): string => {
  const monthIndex = MONTHS.findIndex((m) => m.toLowerCase() === monthName.toLowerCase());
  return monthIndex !== -1 ? (monthIndex + 1).toString() : monthName;
};

// Used only for GRC5253 SDO leases snapshots
// TODO - rename this file to GRC5253SDOReportssnapshotsForm
export function ReportsSnapshotsForm() {
  const [error, setError] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [year, setYear] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [grcReviewCyclePhase, setGRCReviewCyclePhase] = useState<string | undefined>(undefined);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();
  const currentDay = currentDate.getDate();

  const years = Array.from({ length: currentYear - SDO_START_YEAR + 1 }, (_, index) =>
    (SDO_START_YEAR + index).toString()
  );

  const availableMonths = useMemo(() => {
    if (!year) return [];
    return getAvailableMonths(year, currentYear, currentMonthIndex, currentDay).map((m) => ({
      label: m,
      value: m,
    }));
  }, [year, currentYear, currentMonthIndex, currentDay]);

  const phaseOptions = useMemo(() => {
    if (!month || !year) return [];
    return getPhaseOptions(month, year, currentYear, currentMonthIndex, currentDay);
  }, [month, year, currentYear, currentMonthIndex, currentDay]);

  const snapshotInput = useMemo(
    (): GetSnapshotCommandInput => ({
      controlId: 'GRC5253',
      parameters: [
        { key: 'phasename', value: grcReviewCyclePhase },
        { key: 'month', value: monthToNumber(month) },
        { key: 'year', value: year },
        { key: 'leaseType', value: LEASE_TYPES.SDO },
      ],
    }),
    [grcReviewCyclePhase, month, year]
  );

  const {
    data: snapshotData,
    refetch,
    isLoading,
    error: queryError,
  } = useGetGRCReviewCycleSnapshotUrl(snapshotInput, false);

  const isDownloadButtonDisabled = !(year && month && grcReviewCyclePhase);

  const handleYearChange = ({ detail }: any) => {
    if (detail.selectedOption.value) {
      setYear(detail.selectedOption.value);
      setMonth('');
      setGRCReviewCyclePhase(undefined);
    }
  };

  const handleMonthChange = ({ detail }: any) => {
    if (detail.selectedOption.value) {
      setMonth(detail.selectedOption.value);
      setGRCReviewCyclePhase(undefined);
    }
  };

  const handlePhaseChange = ({ detail }: any) => {
    if (detail.selectedOption.value) {
      setGRCReviewCyclePhase(detail.selectedOption.value);
    }
  };

  return (
    <Container header={<Header variant="h2">GRC Review Cycle Snapshot</Header>}>
      <Form
        errorText={error ? 'There was an error generating your snapshot.' : undefined}
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <DownloadButton
              getUrlFunction={() => {
                setError(false);
                setSuccess(false);
                return refetch()
                  .then((result) => {
                    if (result.data) {
                      setSuccess(true);
                    }
                    return result.data;
                  })
                  .catch((err) => {
                    setError(true);
                    throw err;
                  });
              }}
              setError={setError}
              getUrlFunctionProps={{}}
              buttonContent={isLoading ? 'Generating...' : 'Download Snapshot'}
              urlPath="presignedUrl"
              buttonDisabled={isDownloadButtonDisabled || isLoading}
            />
          </SpaceBetween>
        }
      >
        <SpaceBetween size="xl">
          <ColumnLayout columns={3}>
            <FormField label="Year">
              <Select
                placeholder="Select Year"
                selectedOption={year ? { label: year, value: year } : null}
                onChange={handleYearChange}
                options={years.map((y) => ({ label: y, value: y }))}
              />
            </FormField>

            <FormField label="Month">
              <Select
                placeholder="Select Month"
                selectedOption={month ? { label: month, value: month } : null}
                onChange={handleMonthChange}
                options={availableMonths}
                disabled={!year}
              />
            </FormField>

            <FormField label="Phase">
              <Select
                placeholder="Select Phase"
                selectedOption={
                  grcReviewCyclePhase
                    ? {
                        label:
                          phaseOptions.find((o) => o.value === grcReviewCyclePhase)?.label ??
                          grcReviewCyclePhase,
                        value: grcReviewCyclePhase,
                      }
                    : null
                }
                onChange={handlePhaseChange}
                options={phaseOptions}
                disabled={!month}
              />
            </FormField>
          </ColumnLayout>

          {success && (
            <Alert
              dismissAriaLabel="Close alert"
              type="success"
              header="Success"
              onDismiss={() => setSuccess(false)}
            >
              Snapshot generated successfully and download started.
            </Alert>
          )}

          {(error || queryError) && (
            <Alert
              dismissAriaLabel="Close alert"
              type="error"
              header="Error"
              onDismiss={() => setError(false)}
            >
              {queryError?.message || 'There was an error generating your snapshot.'}
            </Alert>
          )}
        </SpaceBetween>
      </Form>
    </Container>
  );
}
